/**
 * Слой светильников на потолке: люстры, споты, плафоны — вид сверху.
 *
 * Светильник стоит в точке плана и рисуется поверх кабелей: кабель приходит в
 * его центр. Слой же показывает группы освещения: выделен выключатель — его
 * светильники подсвечены и соединены с ним тонким пунктиром; выделен светильник —
 * пунктир к выключателям, которые его зажигают. Связи освещения хранятся отдельно;
 * у старых выключателей группа читается из проводки (lightsOfSwitch). Светильник без кабеля подсвечен, как и прибор
 * на стене, пока включён слой проводки.
 */
import type { PointerEvent } from "react";
import {
	wiredEndIds,
	type Fixture,
	type FloorItem,
	type ItemsLayer,
	type WallGraph,
} from "@/shared/plan";
import { cableEndPoint } from "../../plan";
import { CeilingLightShape, SELECTED_COLOR } from "@/widgets/catalog";
import { getCatalogItem, isSwitch } from "@/shared/catalog";
import { lightsOfSwitch, switchesOfLight } from "../../electrical";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";

interface Props {
	graph: WallGraph;
	electrical: ItemsLayer;
	items: FloorItem[];
	mmPerPx: number;
	/** Инструмент установки активен: клики по светильникам не перехватываем */
	placingActive: boolean;
	onLightPointerDown: (e: PointerEvent<Element>, item: FloorItem) => void;
}

export const CeilingLightsLayer2D = ({
	graph,
	electrical,
	items,
	mmPerPx,
	placingActive,
	onLightPointerDown,
}: Props) => {
	const wiring = useProjectStore((s) => s.project.layers.wiring);
	const selection = useEditorStore((s) => s.selection);
	const tool = useEditorStore((s) => s.tool);
	// Инструментом установки клики по светильникам не перехватываем — иначе рядом не поставить новый
	const interactive = tool === Tool.Cable || !placingActive;
	// Слой проводки выключен — кабелей не видно, и подсвечивать «не подключён» нечестно
	const wired = wiring.visible ? wiredEndIds(wiring.items) : new Set<string>();

	// Группа освещения вокруг выделенного: выключатель → его светильники, светильник → его выключатели
	let highlighted = new Set<string>();
	let links: { from: string; to: string }[] = [];
	if (selection?.kind === SelectionKind.Fixture) {
		const fixture: Fixture | undefined = electrical.fixtures[selection.id];
		if (fixture && isSwitch(getCatalogItem(fixture.ref))) {
			const lights = lightsOfSwitch(electrical, wiring.items, selection.id, wiring.connections);
			highlighted = new Set(lights);
			links = lights.map((id) => ({ from: selection.id, to: id }));
		}
	} else if (selection?.kind === SelectionKind.Light && electrical.items[selection.id]) {
		const switches = switchesOfLight(electrical, wiring.items, selection.id, wiring.connections);
		highlighted = new Set(switches);
		links = switches.map((id) => ({ from: id, to: selection.id }));
	}

	const px = (n: number) => n * mmPerPx;

	return (
		<g>
			{/* Связи «выключатель — светильник»: пунктир по прямой, это не кабель, а подсказка о группе */}
			<g style={{ pointerEvents: "none" }}>
				{links.map(({ from, to }) => {
					const a = cableEndPoint(graph, electrical, from);
					const b = cableEndPoint(graph, electrical, to);
					if (!a || !b) return null;
					return (
						<line
							key={`${from}-${to}`}
							x1={a.x}
							y1={a.y}
							x2={b.x}
							y2={b.y}
							stroke={SELECTED_COLOR}
							strokeWidth={px(1)}
							strokeDasharray={`${px(2)} ${px(3)}`}
							opacity={0.7}
						/>
					);
				})}
			</g>

			{items.map((item) => {
				const info = getCatalogItem(item.ref);
				const selected = selection?.kind === SelectionKind.Light && selection.id === item.id;
				return (
					<g key={item.id} transform={`translate(${item.x} ${item.y})`}>
						<CeilingLightShape
							item={item}
							info={info}
							selected={selected}
							unconnected={wiring.visible && !wired.has(item.id)}
							highlighted={highlighted.has(item.id)}
							mmPerPx={mmPerPx}
							interactive={interactive}
							onPointerDown={(e) => onLightPointerDown(e, item)}
						/>
					</g>
				);
			})}
		</g>
	);
};
